import { useEffect } from 'react';
import { useFxRatesStore } from '@/stores/trades/fx-rates-store';

interface RatesQueryResult {
  data?: { rates?: Record<string, number> } | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Shared body for the two FX-rate sync hooks (review CU6): inject the query's rates into the given
 * React-independent module store when they land, and bump the shared fx-rates version so memoised
 * on-page currency roll-ups recompute (otherwise they stay frozen on the cold-load fallback until an
 * unrelated re-render — LR-469). Pass the ALREADY-CALLED query result (each wrapper owns its own
 * useQuery). Returns isLoading/isError plus `hasRates`; each wrapper maps `hasRates` to its own flag.
 */
export const useRatesInjector = (
  result: RatesQueryResult,
  inject: (rates: Record<string, number>) => void
): { isLoading: boolean; isError: boolean; hasRates: boolean } => {
  const { data, isLoading, isError } = result;
  const bumpVersion = useFxRatesStore((s) => s.bumpVersion);
  useEffect(() => {
    if (data?.rates) {
      inject(data.rates);
      bumpVersion();
    }
  }, [data, bumpVersion, inject]);
  return { isLoading, isError, hasRates: !isLoading && !!data?.rates };
};
